import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Target, Trophy, Users, Star, BookOpen, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Home = () => {
  const [featuredExercises, setFeaturedExercises] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedExercises = async () => {
      try {
        // Show only Basic tier exercises in the featured section so the free landing is consistent
        const response = await axios.get(`${API}/exercises?tier=basic&limit=6`);
        setFeaturedExercises(response.data);
      } catch (error) {
        console.error('Error fetching exercises:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedExercises();
  }, []);

  const features = [
    {
      icon: Target,
      title: 'Skill-Focused',
      description: 'Exercises organized by baseball skills - batting, pitching, fielding, and more',
      color: 'text-orange-600 bg-orange-100'
    },
    {
      icon: Trophy,
      title: 'Difficulty Levels',
      description: 'Beginner to advanced drills that grow with your player',
      color: 'text-blue-600 bg-blue-100'
    },
    {
      icon: BookOpen,
      title: 'Step-by-Step',
      description: 'Clear instructions with tips from experienced coaches',
      color: 'text-green-600 bg-green-100'
    },
    {
      icon: TrendingUp,
      title: 'Progressive Training',
      description: 'Build skills systematically with structured exercises',
      color: 'text-purple-600 bg-purple-100'
    }
  ];

  const stats = [
    { number: '15+', label: 'Expert Drills' },
    { number: '5', label: 'Skill Areas' },
    { number: '3', label: 'Difficulty Levels' },
    { number: '100%', label: 'Free Access' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-yellow-50 to-blue-100 opacity-60"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="text-center space-y-8">
            <div className="inline-block">
              <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 text-sm font-semibold border-0">
                <Star className="h-4 w-4 inline mr-1 fill-current" />
                Ages 9-12 • Little League Players
              </Badge>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-black text-gray-900 leading-tight">
              Become a Better
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                Baseball Player
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-medium">
              Master essential baseball skills with fun, engaging exercises designed specifically for young athletes. 
              From batting to pitching, we've got the drills to help you excel!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Link to="/exercises">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-6 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Explore Exercises
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-orange-500 text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg font-bold rounded-xl transition-all duration-200"
              >
                Watch Demo
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="relative bg-white border-y-4 border-orange-400 shadow-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                    {stat.number}
                  </div>
                  <div className="text-sm md:text-base text-gray-600 font-semibold mt-2">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Why Our Training Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Designed by coaches who understand youth baseball
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-2 hover:border-orange-400 transition-all duration-200 hover:shadow-xl transform hover:-translate-y-2 bg-white">
                <CardHeader>
                  <div className={`w-14 h-14 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                    <feature.icon className="h-7 w-7" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900">{feature.title}</CardTitle>
                  <CardDescription className="text-base text-gray-600 leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Exercises */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Popular Exercises
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Start with these fan-favorite drills
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              <div className="col-span-full text-center py-12">
                <p className="text-xl text-gray-600">Loading exercises...</p>
              </div>
            ) : (
              featuredExercises.map((exercise) => (
              <Card key={exercise.id} className="border-2 hover:border-orange-400 transition-all duration-200 hover:shadow-2xl transform hover:-translate-y-2 bg-white overflow-hidden">
                <CardHeader className="space-y-3">
                  <div className="flex items-start justify-between">
                    <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200 font-semibold border border-orange-300">
                      {exercise.skill}
                    </Badge>
                    <Badge 
                      variant="outline" 
                      className={`font-semibold ${
                        exercise.difficulty === 'Beginner' ? 'border-green-500 text-green-700 bg-green-50' :
                        exercise.difficulty === 'Intermediate' ? 'border-blue-500 text-blue-700 bg-blue-50' :
                        'border-red-500 text-red-700 bg-red-50'
                      }`}
                    >
                      {exercise.difficulty}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 leading-tight">
                    {exercise.title}
                  </CardTitle>
                  <CardDescription className="text-base text-gray-600 line-clamp-2">
                    {exercise.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="text-sm text-gray-600 font-medium">
                      <span className="text-orange-600 font-semibold">Duration:</span> {exercise.duration}
                    </div>
                    <Link to={`/exercise/${exercise.id}`}>
                      <Button className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-2.5 rounded-lg transition-all duration-200 transform hover:scale-105">
                        View Exercise
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/exercises">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-4 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                View All Exercises
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="border-4 border-orange-400 bg-gradient-to-br from-orange-50 to-yellow-50 overflow-hidden shadow-2xl">
            <CardContent className="p-12 text-center space-y-6">
              <Users className="h-16 w-16 text-orange-600 mx-auto" />
              <h2 className="text-3xl md:text-4xl font-black text-gray-900">
                Ready to Level Up Your Game?
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed">
                Join thousands of young players improving their skills every day with our expert-designed exercises.
              </p>
              <Link to="/exercises">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-10 py-6 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Start Training Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};